import React, { useContext, useEffect, useState } from "react";
import i18next from "i18next";
import { Box, CircularProgress, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { Context } from "states/Store";
import { getAccountConsumption } from "services/edge/ConsumptionService";
import { BRAND, FONT, RADIUS, useLightStyles } from "./lightTheme";
import moment from "moment";

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CFG = {
  OK:      { label: () => i18next.t("light.consumption.withinLimits"), color: "#15803D", bg: "#DCFCE7", bar: "#22C55E" },
  WARNING: { label: () => i18next.t("light.consumption.approaching"),  color: "#B45309", bg: "#FEF3C7", bar: "#F59E0B" },
  REACHED: { label: () => i18next.t("light.consumption.reached"),      color: "#B91C1C", bg: "#FEE2E2", bar: "#EF4444" },
};

const useStyles = makeStyles()(() => ({
  // ── Top row: period + badge ──
  topRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
  },
  period: {
    ...FONT.small,
    color: BRAND.textMuted,
    fontFamily: FONT.family,
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.35rem",
    borderRadius: "20px",
    padding: "0.3rem 0.85rem",
    ...FONT.tiny,
    fontFamily: FONT.family,
    flexShrink: 0,
    letterSpacing: "0.02em",
  },
  statusDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    flexShrink: 0,
  },

  // ── Center metric ──
  metricBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: "0.5rem",
    padding: "1.5rem 0",
  },
  metricLabel: {
    ...FONT.label,
    color: BRAND.textMuted,
    fontFamily: FONT.family,
  },
  metricValue: {
    fontSize: "5rem",
    fontWeight: 800,
    color: BRAND.textMain,
    fontFamily: FONT.family,
    letterSpacing: "-0.05em",
    lineHeight: 1,
  },
  metricSub: {
    ...FONT.body,
    color: BRAND.textMuted,
    fontFamily: FONT.family,
    fontWeight: 400,
  },

  // ── Progress (with threshold) ──
  progressBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "0.6rem",
    padding: "0 1.25rem 1.25rem",
  },
  progressMeta: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
  },
  progressFraction: {
    ...FONT.small,
    color: BRAND.textMuted,
    fontFamily: FONT.family,
  },
  progressPct: {
    ...FONT.small,
    fontWeight: 700,
    color: BRAND.textMain,
    fontFamily: FONT.family,
  },
  progressTrack: {
    height: "8px",
    borderRadius: RADIUS.sm,
    backgroundColor: "#F1F5F9",
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    borderRadius: RADIUS.sm,
    transition: "width 1s cubic-bezier(0.16,1,0.3,1)",
  },

  // ── No threshold note ──
  noThreshold: {
    textAlign: "center",
    ...FONT.small,
    color: BRAND.textMuted,
    fontFamily: FONT.family,
    fontStyle: "italic",
    padding: "0 1.25rem 1.25rem",
  },

  // ── Loading ──
  centered: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "40vh",
    gap: "1rem",
    color: BRAND.textMuted,
    fontFamily: FONT.family,
    ...FONT.body,
  },
}));

// ─── Component ────────────────────────────────────────────────────────────────
export default function LightConsumption({ embedded = false }) {
  const { classes: s } = useStyles();
  const { classes: light } = useLightStyles();
  const [state, dispatch] = useContext(Context);
  const [loading, setLoading] = useState(true);
  const [consumption, setConsumption] = useState(null);

  useEffect(() => {
    if (!state.account?.id) return;
    setLoading(true);
    (async () => {
      try {
        const res = await getAccountConsumption(state.account.id, dispatch);
        setConsumption(res?.content ?? null);
      } catch (_) {
        setConsumption(null);
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.account?.id]);

  const cfg = STATUS_CFG[consumption?.status] ?? null;
  const count = consumption?.thirdPartyCount ?? 0;
  const threshold = consumption?.thirdPartyThreshold ?? null;
  const pct = threshold ? Math.min(100, Math.round((count / threshold) * 100)) : null;

  const fmt = (d) => d ? moment(d).format("DD/MM/YYYY") : null;
  const period =
    consumption?.periodStartDate && consumption?.periodEndDate
      ? `${fmt(consumption.periodStartDate)} \u2192 ${fmt(consumption.periodEndDate)}`
      : null;

  const content = (
    <>
          {loading ? (
            <Box className={s.centered}>
              <CircularProgress size={28} style={{ color: BRAND.primary }} />
              <span>{i18next.t("light.common.loading")}</span>
            </Box>
          ) : (
            <Box className={light.sectionCard}>
              {/* ── Period + status ── */}
              <Box className={s.topRow} style={{ padding: "1.25rem 1.25rem 0" }}>
                <Typography className={s.period}>
                  {period ?? i18next.t("light.consumption.periodUnavailable")}
                </Typography>
                {cfg && (
                  <Box className={s.statusBadge} style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                    <Box className={s.statusDot} style={{ backgroundColor: cfg.color }} />
                    {cfg.label()}
                  </Box>
                )}
              </Box>

              {/* ── Big metric ── */}
              <Box className={s.metricBlock}>
                <Typography className={s.metricLabel}>{i18next.t("light.consumption.consumed")}</Typography>
                <Typography className={s.metricValue}>{count.toLocaleString("fr-FR")}</Typography>
                {threshold != null && (
                  <Typography className={s.metricSub}>{i18next.t("light.consumption.threshold", { max: threshold.toLocaleString("fr-FR") })}</Typography>
                )}
              </Box>

              {/* ── Progress bar (if threshold) or note ── */}
              {threshold != null && pct != null ? (
                <Box className={s.progressBlock}>
                  <Box className={s.progressMeta}>
                    <Typography className={s.progressFraction}>
                      {count.toLocaleString("fr-FR")} / {threshold.toLocaleString("fr-FR")} {i18next.t("light.consumption.thirdparties")}
                    </Typography>
                    <Typography className={s.progressPct}>{pct}%</Typography>
                  </Box>
                  <Box className={s.progressTrack}>
                    <Box
                      className={s.progressBar}
                      style={{ width: `${pct}%`, backgroundColor: cfg?.bar ?? BRAND.primary }}
                    />
                  </Box>
                </Box>
              ) : (
                <Typography className={s.noThreshold}>{i18next.t("light.consumption.noThreshold")}</Typography>
              )}
            </Box>
          )}
    </>
  );

  if (embedded) return content;

  return (
    <Box className={light.pageRoot}>
      <Box className={light.pageHeader}>
        <Box className={light.pageHeaderInner}>
          <Typography className={light.pageTitleWhite}>{i18next.t("light.consumption.title")}</Typography>
          <Typography className={light.pageSubtitleWhite}>{i18next.t("light.consumption.period")}</Typography>
        </Box>
      </Box>
      <Box className={light.pageInner} style={{ marginTop: "-0.75rem", maxWidth: "600px" }}>
        {content}
      </Box>
    </Box>
  );
}
